import { Alert, Card, Col, Row, Skeleton, Statistic, Table, Tag, Typography } from 'antd';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useDashboard } from '../api/hooks';
import { errorMessage } from '../api/client';
import { date, money } from '../utils/format';
import type { RecentTransaction } from '../types';

const TYPE_COLOR: Record<string, string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
  'Profit Payment': 'green',
};

export default function DashboardPage() {
  const { data, isLoading, error } = useDashboard();

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !data) return <Skeleton active paragraph={{ rows: 8 }} />;

  const kpis = [
    { title: 'Total investors', value: data.totalInvestors, suffix: `/ ${data.activeInvestors} active` },
    { title: 'Total capital', value: money(data.totalCapital), prefix: 'PKR' },
    { title: "Today's due profit", value: money(data.todayDueProfit), prefix: 'PKR' },
    { title: 'Paid this month', value: money(data.thisMonthPaidProfit), prefix: 'PKR' },
    { title: 'Outstanding profit', value: money(data.outstandingProfit), prefix: 'PKR' },
    { title: 'Capital + liabilities', value: money(data.totalCapitalPlusLiabilities), prefix: 'PKR' },
  ];

  return (
    <>
      <Typography.Title level={3}>Investment Dashboard</Typography.Title>

      <Row gutter={[16, 16]}>
        {kpis.map((kpi) => (
          <Col key={kpi.title} xs={24} sm={12} lg={8} xl={4}>
            <Card>
              <Statistic
                title={kpi.title}
                value={kpi.value}
                prefix={kpi.prefix}
                suffix={kpi.suffix}
                valueStyle={{ fontSize: 20 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card title="Profit paid — last 12 months" style={{ marginTop: 16 }}>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data.monthlyPaidProfit} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tickLine={false} />
              <YAxis tickFormatter={(v: number) => money(v)} width={90} tickLine={false} />
              <Tooltip formatter={(v: number) => `PKR ${money(v)}`} />
              <Bar dataKey="amount" fill="#1677ff" radius={[4, 4, 0, 0]} name="Profit paid" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card title="Recent transactions" style={{ marginTop: 16 }}>
        <Table<RecentTransaction>
          dataSource={data.recentTransactions}
          rowKey={(row, index) => `${row.investorCode}-${row.date}-${index}`}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Date', dataIndex: 'date', render: date },
            {
              title: 'Type',
              dataIndex: 'type',
              render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{value}</Tag>,
            },
            { title: 'Code', dataIndex: 'investorCode' },
            { title: 'Investor', dataIndex: 'investorName' },
            { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
          ]}
        />
      </Card>
    </>
  );
}
